import { useCallback, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { joiResolver } from '@hookform/resolvers/joi';

import { useAppContext } from '../../../app/Context';
import { useDrawerContext } from '../../../components/Drawer/Context';
import { getValidCourses } from '../../../db/courses';
import { CourseListItem } from '../../../db/courses/types';
import Logger from '../../../utils/logger';
import { FormValues } from './types';
import { validationSchema } from './validators';
import { getPinnedCourses, setPinnedCourses } from '../../../db/shared/preferences';

export interface UseTileCoursesForm {
  onNext: (data: { selectedCourse: CourseListItem }) => Promise<void> | void;
}

const useTileCoursesForm = ({ onNext }: UseTileCoursesForm) => {
  const { setError } = useAppContext();
  const { onClose } = useDrawerContext();

  const formMethods = useForm<FormValues>({
    resolver: joiResolver(validationSchema),
    defaultValues: {
      selectedCourseId: null,
      pinSelectedCourse: false,
    },
    mode: 'all',
  });

  const { setValue } = formMethods;

  /** Courses */

  const [isFetchingCourses, setIsFetchingCourses] = useState(true);
  const [courses, setCourses] = useState<CourseListItem[]>([]);

  const fetchCourses = useCallback(async () => {
    setIsFetchingCourses(true);
    try {
      const { courses } = await getValidCourses();

      const pinnedCourseIds = getPinnedCourses();

      const sortedCourses = courses.sort((a, b) => {
        const isPinnedCourseA = pinnedCourseIds.includes(a.courseId);
        const isPinnedCourseB = pinnedCourseIds.includes(b.courseId);
        return Number(isPinnedCourseB) - Number(isPinnedCourseA);
      });

      setCourses(sortedCourses);
    } catch (error: any) {
      setError(error.message);
      Logger.error(error);
    } finally {
      setIsFetchingCourses(false);
    }
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const onSubmit: SubmitHandler<FormValues> = useCallback(
    (data) => {
      const { selectedCourseId, pinSelectedCourse } = data;

      if (pinSelectedCourse) {
        setPinnedCourses(selectedCourseId ?? '');
      }

      const selectedCourse = courses.find((course) => course.courseId === selectedCourseId)!;

      onNext({ selectedCourse });
    },
    [courses, onNext]
  );

  useEffect(() => {
    /** select first course when there is only 1 course */
    if (courses.length !== 1) return;
    const firstCourseId = courses[0].courseId;
    setValue('selectedCourseId', firstCourseId, { shouldValidate: true });
  }, [courses, setValue]);

  return { isFetchingCourses, courses, formMethods, onSubmit, onClose };
};

export default useTileCoursesForm;
